export const AGENT_READY_HOST = "agentready.siteglue.ai";
export const AGENT_READY_ORIGIN = "https://agentready.siteglue.ai";
export const SITEGLUE_WWW_ORIGIN = "https://www.siteglue.ai";

/**
 * www /agent-ready stays on www until agentready.siteglue.ai answers over HTTPS.
 * Turn this on only after that certificate is confirmed.
 */
export const AGENT_READY_WWW_REDIRECT_ENABLED = false;

export function isAgentReadyHost(hostname: string): boolean {
  const host = hostname.split(":")[0]?.toLowerCase() ?? "";
  return host === AGENT_READY_HOST;
}

export function scannerHomeHref(scannerHost: boolean): string {
  return scannerHost ? "/" : "/agent-ready";
}

/** Prefill the public scanner wizard. Reuses the existing /agent-ready?url= convention. */
export function scannerCustomizeHref(url: string, scannerHost = false): string {
  const path = scannerHomeHref(scannerHost);
  const trimmed = url.trim();
  if (!trimmed) return path;
  const params = new URLSearchParams({ url: trimmed });
  return `${path}?${params.toString()}`;
}

export const REPORT_TABS = ["agents", "conversion", "aeo", "channels", "agents-test", "improve"] as const;
export type ReportTab = (typeof REPORT_TABS)[number];
export const DEFAULT_REPORT_TAB: ReportTab = "agents";

const REPORT_TAB_ALIASES: Record<string, ReportTab> = {
  people: "conversion",
  hro: "conversion",
  walk: "agents-test",
  watch: "improve",
  visibility: "aeo",
};

export function parseReportTab(value: string | null | undefined): ReportTab {
  const normalized = value?.trim().toLowerCase() ?? "";
  if (!normalized) return DEFAULT_REPORT_TAB;
  if (REPORT_TAB_ALIASES[normalized]) return REPORT_TAB_ALIASES[normalized];
  return (REPORT_TABS as readonly string[]).includes(normalized)
    ? (normalized as ReportTab)
    : DEFAULT_REPORT_TAB;
}

export function visibleReportTabs(scope: {
  aro?: boolean;
  cro?: boolean;
  aeo?: boolean;
  channels?: boolean;
  agentsTest?: boolean;
  /** Historical People/Channel reports only. cro-v5 uses a Channel subview. */
  legacyChannels?: boolean;
}): ReportTab[] {
  const tabs: ReportTab[] = [];
  if (scope.aro !== false) tabs.push("agents");
  if (scope.cro !== false) tabs.push("conversion");
  if (scope.aeo) tabs.push("aeo");
  if (scope.legacyChannels) tabs.push("channels");
  if (scope.agentsTest) tabs.push("agents-test");
  return tabs.length ? tabs : ["agents", "conversion"];
}

export function parseImproveFilter(value: string | null | undefined): "all" | "quick_wins" | "highest_impact" | "needs_verification" {
  const normalized = value?.trim().toLowerCase() ?? "";
  if (normalized === "quick_wins" || normalized === "highest_impact" || normalized === "needs_verification") {
    return normalized;
  }
  return "all";
}

export function scannerReportHref(
  id: string,
  scannerHost: boolean,
  query?: { tab?: string | null; view?: string | null; issue?: string | null; filter?: string | null },
): string {
  const path = scannerHost ? `/report/${id}` : `/agent-ready/report/${id}`;
  const params = new URLSearchParams();
  const view = query?.view?.trim();
  if (view) params.set("view", view);
  if (query?.tab != null && query.tab !== "") params.set("tab", parseReportTab(query.tab));
  const issue = query?.issue?.trim();
  if (issue) params.set("issue", issue);
  if (query?.filter && query.filter !== "all") params.set("filter", parseImproveFilter(query.filter));
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

export function scannerOpeningHref(scannerHost: boolean): string {
  return scannerHost ? "/opening" : "/agent-ready/opening";
}

export function scannerWalkHref(id: string, scannerHost: boolean, presetId?: string): string {
  const path = scannerHost ? `/report/${id}/walk` : `/agent-ready/report/${id}/walk`;
  return presetId ? `${path}?preset=${encodeURIComponent(presetId)}` : path;
}

function withSearch(url: string, search: string): string {
  if (!search) return url;
  return `${url}${search.startsWith("?") ? search : `?${search}`}`;
}

export type AgentReadyRoute =
  | { kind: "rewrite"; pathname: string }
  | { kind: "redirect"; url: string; status: 301 };

/** Public paths on agentready.siteglue.ai. API and sitemap stay on this app. */
export function routeAgentReadyPath(pathname: string, search = ""): AgentReadyRoute | null {
  if (pathname === "/sitemap.xml" || pathname === "/robots.txt") return null;
  if (pathname === "/favicon.ico") {
    return { kind: "rewrite", pathname: "/siteglue-favicon-32.png" };
  }
  if (pathname === "/" ) {
    return { kind: "rewrite", pathname: "/agent-ready" };
  }
  if (pathname === "/agent-ready" || pathname === "/agent-ready/") {
    return { kind: "redirect", url: withSearch(`${AGENT_READY_ORIGIN}/`, search), status: 301 };
  }
  const legacyReport = pathname.match(/^\/agent-ready\/report\/([^/]+)\/?$/);
  if (legacyReport?.[1]) {
    return {
      kind: "redirect",
      url: withSearch(`${AGENT_READY_ORIGIN}/report/${legacyReport[1]}`, search),
      status: 301,
    };
  }
  if (pathname === "/scan-my-business" || pathname === "/scan-my-business/") {
    return { kind: "rewrite", pathname: "/agent-ready" };
  }
  if (pathname === "/opening" || pathname === "/opening/") {
    return { kind: "rewrite", pathname: "/agent-ready/opening" };
  }
  const walk = pathname.match(/^\/report\/([^/]+)\/(?:walk|interact)\/?$/);
  if (walk?.[1]) {
    return { kind: "rewrite", pathname: `/agent-ready/report/${walk[1]}/walk` };
  }
  const report = pathname.match(/^\/report\/([^/]+)\/?$/);
  if (report?.[1]) {
    return { kind: "rewrite", pathname: `/agent-ready/report/${report[1]}` };
  }
  return {
    kind: "redirect",
    url: withSearch(`${SITEGLUE_WWW_ORIGIN}${pathname}`, search),
    status: 301,
  };
}

/** 301 target for www.siteglue.ai/agent-ready. Null while the redirect is gated off. */
export function agentReadyWwwRedirectTarget(pathname: string, search = ""): string | null {
  if (!AGENT_READY_WWW_REDIRECT_ENABLED) return null;
  if (
    pathname === "/agent-ready" ||
    pathname === "/agent-ready/" ||
    pathname === "/agent-ready/scan-my-business" ||
    pathname === "/agent-ready/scan-my-business/"
  ) {
    return withSearch(`${AGENT_READY_ORIGIN}/`, search);
  }
  const interact = pathname.match(/^\/agent-ready\/report\/([^/]+)\/interact\/?$/);
  if (interact?.[1]) return withSearch(`${AGENT_READY_ORIGIN}/report/${interact[1]}/walk`, search);
  const report = pathname.match(/^\/agent-ready\/report\/([^/]+)\/?$/);
  if (report?.[1]) return withSearch(`${AGENT_READY_ORIGIN}/report/${report[1]}`, search);
  return null;
}
